import * as React from "react";

import {
  Stage,
  StageChartHeading,
  StageFacts,
  StageHeadline,
  StageLead,
  StageMeta,
} from "@/components/site/sections/six-pillars-preview/stage";
import { sixPillarsFundamentalsPreview } from "@/lib/six-pillars-preview";
import {
  formatPercent,
  formatScore,
  formatShortDate,
  scalePosition,
  scoreBand,
  type ScoreBand,
} from "@/lib/six-pillars-stage";
import { cn } from "@/lib/utils";

// The Fundamentals lead page's stage (cooperly_app pages/fundamentals/lead/stats.tsx
// and features/fundamentals/fundamentals-category-chart.tsx): the weakest area as
// the headline and one score dot per area on the 1–5 scale.

/** The five areas from the top of the pyramid (results) down to its base (trust). */
const PYRAMID_ORDER = ["Results", "Accountability", "Commitment", "Conflict", "Trust"];

const TICKS = [1, 2, 3, 4, 5];
const SCALE_LABELS = ["almost never", "rarely", "sometimes", "usually", "almost always"];

const LEGEND = [
  { label: "below 3", className: "bg-[#E5664A]" },
  { label: "3–4", className: "bg-[#d4d4d8]" },
  { label: "4 and above", className: "bg-[#28A68E]" },
];

const LINE_CLASS: Record<ScoreBand, string> = {
  low: "bg-[#E5664A]",
  mid: "bg-white/35",
  high: "bg-[#28A68E]",
};

const DOT_CLASS: Record<ScoreBand, string> = {
  low: "bg-[#E5664A]",
  mid: "bg-[#d4d4d8]",
  high: "bg-[#28A68E]",
};

const ROW_GRID =
  "grid grid-cols-[20px_minmax(0,1fr)_2.5rem] gap-x-3 @min-[30rem]:grid-cols-[20px_8rem_minmax(0,1fr)_2.5rem]";

export function FundamentalsStage() {
  const headingId = React.useId();
  const chartTitleId = React.useId();
  const { categories, completion, shownAnswers, schedule, weakestArea } =
    sixPillarsFundamentalsPreview;

  const rows = [...categories].sort(
    (a, b) => PYRAMID_ORDER.indexOf(a.category) - PYRAMID_ORDER.indexOf(b.category),
  );
  // Ties go to the area nearer the base for the weakest and nearer the top for the strongest.
  const weakest = [...rows]
    .reverse()
    .reduce((lowest, row) => (row.average < lowest.average ? row : lowest));
  const strongest = rows.reduce((highest, row) =>
    row.average > highest.average ? row : highest,
  );
  const lowestStatement =
    weakestArea.category === weakest.category
      ? weakestArea.statements.reduce((lowest, statement) =>
          Number(statement.meta) < Number(lowest.meta) ? statement : lowest,
        )
      : null;

  return (
    <Stage
      labelledBy={headingId}
      contentClassName="flex-[1_1_320px]"
      asideClassName="flex-[1_1_480px]"
      aside={
        <figure aria-labelledby={chartTitleId} className="@container m-0 flex flex-col gap-3.5">
          <StageChartHeading
            title="Average score by area"
            titleId={chartTitleId}
            legend={LEGEND}
            swatch="dot"
          />
          <ul className="m-0 flex list-none flex-col p-0">
            {rows.map((row) => (
              <FundamentalsAreaRow
                key={row.category}
                label={row.category}
                value={row.average}
                level={PYRAMID_ORDER.length - PYRAMID_ORDER.indexOf(row.category)}
                highlighted={row === weakest}
              />
            ))}
          </ul>
          <FundamentalsScale />
        </figure>
      }
    >
      <StageHeadline
        id={headingId}
        value={weakest.category}
        caption={`weakest area — ${formatScore(weakest.average)} of 5`}
      />
      {lowestStatement ? (
        <StageLead>
          The team gave its lowest score to “{lowestStatement.text.replace(/[.\s]+$/u, "")}”:{" "}
          {formatScore(Number(lowestStatement.meta))} of 5.
        </StageLead>
      ) : null}
      <StageFacts
        items={[
          {
            key: "strongest",
            label: "Strongest",
            value: strongest.category,
            detail: `${formatScore(strongest.average)} of 5`,
          },
          {
            key: "shown-completion",
            label: "Answers in shown cycles",
            value: formatPercent((shownAnswers.completed / shownAnswers.total) * 100),
            detail: `${shownAnswers.completed} of ${shownAnswers.total}`,
          },
          {
            key: "latest-completion",
            label: "Latest completion",
            value: formatPercent(completion.percent),
            detail: `${completion.answered} of ${completion.available}`,
          },
        ]}
      />
      <StageMeta
        items={[
          `Previous cycle ${formatShortDate(schedule.previousCycle)}`,
          `Next cycle ${formatShortDate(schedule.nextCycle)}`,
          schedule.rhythm,
        ]}
      />
    </Stage>
  );
}

function FundamentalsAreaRow({
  label,
  value,
  level,
  highlighted,
}: {
  label: string;
  value: number;
  level: number;
  highlighted: boolean;
}) {
  const band = scoreBand(value);
  const position = `${scalePosition(value) * 100}%`;

  return (
    <li
      className={cn(
        ROW_GRID,
        "items-center rounded-[10px] py-2.5 pr-3.5 pl-3 @min-[30rem]:h-[46px] @min-[30rem]:py-0",
        highlighted && "bg-white/[0.07]",
      )}
    >
      <PyramidLevelGlyph level={level} />
      <span
        className={cn(
          "min-w-0 text-sm leading-tight break-words",
          highlighted ? "font-semibold text-white" : "font-medium text-white/80",
        )}
      >
        {label}
      </span>
      <span
        aria-hidden="true"
        className="relative col-span-2 col-start-2 row-start-2 mt-2.5 h-3 @min-[30rem]:col-span-1 @min-[30rem]:col-start-3 @min-[30rem]:row-start-1 @min-[30rem]:mt-0 @min-[30rem]:h-full"
      >
        <span className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-white/[0.08] @min-[30rem]:hidden" />
        {TICKS.map((tick) => (
          <span
            key={tick}
            className="absolute inset-y-0 hidden w-px bg-white/[0.08] @min-[30rem]:block"
            style={{ left: `${scalePosition(tick) * 100}%` }}
          />
        ))}
        <span
          className={cn(
            "absolute top-1/2 left-0 h-[3px] -translate-y-1/2 rounded-full",
            LINE_CLASS[band],
          )}
          style={{ width: position }}
        />
        <span
          className={cn(
            "absolute top-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2",
            highlighted ? "border-[#1f1f23]" : "border-[#18181c]",
            DOT_CLASS[band],
          )}
          style={{ left: position }}
        />
      </span>
      <span
        className={cn(
          "col-start-3 row-start-1 text-right text-[15px] font-semibold tabular-nums @min-[30rem]:col-start-4",
          highlighted ? "text-white" : "text-white/90",
        )}
      >
        <span className="sr-only">: </span>
        {formatScore(value)}
        <span className="sr-only"> of 5</span>
      </span>
    </li>
  );
}

function FundamentalsScale() {
  return (
    <div aria-hidden="true" className={cn(ROW_GRID, "pr-3.5 pl-3 @min-[30rem]:pt-0.5")}>
      <span className="relative col-span-2 col-start-2 h-4 @min-[30rem]:col-span-1 @min-[30rem]:col-start-3 @min-[30rem]:h-9">
        {TICKS.map((tick, index) => (
          <span
            key={tick}
            className="absolute top-0 flex -translate-x-1/2 flex-col items-center text-[11px] leading-tight whitespace-nowrap"
            style={{ left: `${scalePosition(tick) * 100}%` }}
          >
            <span className="text-white/60 tabular-nums">{tick}</span>
            <span className="hidden text-white/50 @min-[30rem]:block">
              {SCALE_LABELS[index]}
            </span>
          </span>
        ))}
      </span>
      {/* A narrow track has no room for five words under the ticks, so they follow as one wrapping line. */}
      <p className="col-span-2 col-start-2 m-0 mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] leading-tight text-white/50 @min-[30rem]:hidden">
        {SCALE_LABELS.map((label, index) => (
          <span key={label} className="whitespace-nowrap">
            <span className="text-white/60 tabular-nums">{TICKS[index]}</span> {label}
          </span>
        ))}
      </p>
    </div>
  );
}

/** A five-step pyramid with the area's own step lit: bottom step for trust, top step for results. */
function PyramidLevelGlyph({ level }: { level: number }) {
  const widths = [20, 16.4, 12.8, 9.2, 5.6];

  return (
    <svg aria-hidden="true" viewBox="0 0 20 15.6" width="20" height="15.6" className="text-white">
      {widths.map((width, index) => (
        <rect
          key={width}
          x={(20 - width) / 2}
          y={13.2 - index * 3.3}
          width={width}
          height={2.4}
          rx={0.6}
          fill="currentColor"
          opacity={index + 1 === level ? 1 : 0.2}
        />
      ))}
    </svg>
  );
}
